import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Footer from "./Footer";
import config from "../config";

const { API_BASE, UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

const ICON_FILES = [
  "ac-install.svg",
  "dust.svg",
  "emergency-repair.svg",
  "heating.svg",
];


const SERVED_LIST_HEADERS = {
  household: "Residential Properties We Serve",
  commercial: "Industries We Serve",
  industrial: "Industries We Serve",
  dehumidifiersales: "Industries We Serve",
};

function getServedHeader(slug = "") {
  return SERVED_LIST_HEADERS[slug.toLowerCase()] || null;
}

function parseSpecialityDescription(raw = "", servedHeader = null) {
  if (!raw) return { intro: "", features: [], benefits: [], servedList: null };

  const lines = raw.split("\n");
  const features = [];
  const benefits = [];
  let introLines = [];

  let currentTitle = null;
  let currentLines = [];
  let inSection = false;
  let inBenefits = false;

  let servedList = null;
  let inServedList = false;

  const isTitleLine = (line) => {
    const t = line.trim();
    return t.endsWith(":") && t.length < 60 && !t.includes(". ");
  };

  const isBenefitsHeader = (line) => {
    const t = line.trim().replace(/:$/, "").trim().toLowerCase();
    return t === "benefits of our service";
  };

  const isServedHeader = (line) => {
    if (!servedHeader) return false;
    const t = line.trim().replace(/:$/, "").trim().toLowerCase();
    return t === servedHeader.trim().toLowerCase();
  };

  const isBulletLine = (line) => /^[-•]\s*/.test(line.trim());

  const flushSection = () => {
    if (currentTitle === null) return;
    const desc = currentLines.join(" ").trim();
    if (inBenefits) {
      benefits.push({ label: currentTitle, desc });
    } else {
      features.push({ label: currentTitle, desc });
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    if (isServedHeader(trimmed)) {
      flushSection();
      currentTitle = null;
      currentLines = [];
      inSection = false;
      inBenefits = false;
      inServedList = true;
      servedList = { label: trimmed.replace(/:$/, "").trim(), items: [] };
      continue;
    }

    if (inServedList) {
      if (isBulletLine(trimmed)) {
        servedList.items.push(trimmed.replace(/^[-•]\s*/, "").trim());
        continue;
      }
      if (isTitleLine(trimmed) || isBenefitsHeader(trimmed)) {
        inServedList = false;
      } else {
        continue;
      }
    }

    if (isBenefitsHeader(trimmed)) {
      flushSection();
      inBenefits = true;
      currentTitle = null;
      currentLines = [];
      inSection = true;
      continue;
    }

    if (isTitleLine(trimmed)) {
      const titleText = trimmed.replace(/:$/, "").trim();
      flushSection();
      inSection = true;
      currentTitle = titleText;
      currentLines = [];
    } else if (inSection) {
      currentLines.push(trimmed);
    } else {
      introLines.push(trimmed);
    }
  }
  flushSection();

  return { intro: introLines.join(" ").trim(), features, benefits, servedList };
}

/* ── Extract a YouTube video ID from any common URL shape ── */
function getYouTubeId(url = "") {
  const match = String(url).match(
    /(?:youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : null;
}

/* ── Icons ── */
const CheckDotIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 8.2l2.8 2.8 6.2-6.2" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ── Breadcrumb ── */
function Breadcrumb({ parent, serviceTitle }) {
  return (
    <nav className="sd-bread" aria-label="Breadcrumb">
      <a href="/" className="sd-bread__link">Home</a>
      <span className="sd-bread__sep" aria-hidden="true">›</span>
      {parent && (
        <>
          <a href={`/${parent.link}`} className="sd-bread__link">{parent.label}</a>
          <span className="sd-bread__sep" aria-hidden="true">›</span>
        </>
      )}
      <span className="sd-bread__current">{serviceTitle}</span>
    </nav>
  );
}

function BrochureCard({ products }) {
  if (!products || products.length === 0) return null;

  return (
    <div className="sd-brochure">
      <h3 className="sd-brochure__title">Download Brochure</h3>
      <div className="sd-brochure__list">
        {products.map((product) => (
          <div key={product.slug} className="sd-brochure__row">
            <span className="sd-brochure__row-label" title={product.title}>
              {product.title}
            </span>
            <div className="sd-brochure__row-actions">
              <a
                href={product.brochure_url}
                target="_blank"
                rel="noopener noreferrer"
                className="sd-brochure__icon-btn sd-brochure__icon-btn--view"
                aria-label={`View brochure for ${product.title}`}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M10 4.5C6 4.5 2.5 10 2.5 10S6 15.5 10 15.5 17.5 10 17.5 10 14 4.5 10 4.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                  <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.6"/>
                </svg>
              </a>
              <a
                href={product.brochure_url}
                download
                className="sd-brochure__icon-btn sd-brochure__icon-btn--download"
                aria-label={`Download brochure for ${product.title}`}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M10 3v10m0 0-3.5-3.5M10 13l3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 15.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmergencyCta({ contact }) {
  return (
    <div className="sd-emergency">
      <div className="sd-emergency__overlay" />
      <div className="sd-emergency__content">
        <h3 className="sd-emergency__title">Emergency HVAC Service</h3>
        <p className="sd-emergency__text">Available 24/7 for urgent repair needs.</p>
        {contact?.phone_number ? (
          <a href={`tel:${contact.phone_number}`} className="sd-emergency__btn">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328z"/>
            </svg>
            Call Now
          </a>
        ) : (
          <a href="/contact-us" className="sd-emergency__btn">Call Now</a>
        )}
      </div>
    </div>
  );
}

function ServedList({ servedList }) {
  if (!servedList || !servedList.items.length) return null;
  return (
    <div className="sd-served">
      <h3 className="sd-served__title">{servedList.label}</h3>
      <ul className="sd-served__list">
        {servedList.items.map((item, i) => (
          <li key={i} className="sd-served__item">
            <span className="sd-served__icon" aria-hidden="true"><CheckDotIcon /></span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Dark "Service List" sidebar ── */
function ServiceList({ siblings, currentSlug }) {
  if (!siblings.length) return null;
  return (
    <div className="sd-list-card">
      <div className="sd-list-card__head">Service List</div>
      <div className="sd-list-card__list">
        {siblings.map((s) => {
          const active = s.link === currentSlug;
          return (
            <a
              key={s.link}
              href={`/services/${s.link}`}
              className={`sd-list-card__item ${active ? "sd-list-card__item--active" : ""}`}
            >
              <span>{s.label}</span>
              <span className="sd-list-card__arrow" aria-hidden="true">
                <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                  <path d="M3 11L11 3M11 3H4M11 3V10" stroke="currentColor" strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

/* ── Sidebar wrapper ── */
function Sidebar({ siblings, currentSlug, contact, servedList, brochureProducts }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      <button
        className="sd-side__mobile-tab"
        onClick={() => setMobileOpen((v) => !v)}
        aria-label={mobileOpen ? "Close services menu" : "Open services menu"}
        aria-expanded={mobileOpen}
      >
        <svg
          width="16" height="16" viewBox="0 0 16 16" fill="none"
          className={`sd-side__mobile-tab-icon ${mobileOpen ? "sd-side__mobile-tab-icon--open" : ""}`}
        >
          <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="sd-side__mobile-tab-label">Services</span>
      </button>

      {/* Overlay rendered in JSX (not ::before pseudo-element) */}
      {mobileOpen && (
        <div
          className="sd-side__overlay"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside className="sd-side">
        <div className={`sd-side__services ${mobileOpen ? "sd-side__services--open" : ""}`}>
          <ServiceList siblings={siblings} currentSlug={currentSlug} />
          {/* Hidden on small screens — these move to the bottom of
              <main> instead, rendered in sd-main__extras. */}
          <div className="sd-side__extras">
            <BrochureCard products={brochureProducts} />
            <EmergencyCta contact={contact} />
            <ServedList servedList={servedList} />
          </div>
        </div>
      </aside>
    </>
  );
}

/* ── "What We Offer" feature grid ── */
function WhatWeOffer({ features }) {
  if (!features.length) return null;
  return (
    <div className="sd-section">
      <h3 className="sd-section__heading">What We Offer</h3>
      <div className="sd-feat__grid">
        {features.map((f, i) => (
          <div key={i} className="sd-feat__card">
            <div className="sd-feat__icon">
              <img
                src={`${UPLOADS_URL}/svg-icons/${ICON_FILES[i % ICON_FILES.length]}`}
                alt=""
                aria-hidden="true"
                onError={(e) => { e.currentTarget.style.opacity = "0"; }}
              />
            </div>
            <h4 className="sd-feat__title">{f.label}</h4>
            <p className="sd-feat__desc">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Benefits({ benefits }) {
  if (!benefits.length) return null;
  return (
    <div className="sd-section">
      <h3 className="sd-section__heading">Benefits of Our Service</h3>
      <div className="sd-benefits__grid">
        {benefits.map((b, i) => (
          <div key={i} className="sd-benefits__item">
            <span className="sd-benefits__icon" aria-hidden="true"><CheckDotIcon /></span>
            <div className="sd-benefits__content">
              <h4 className="sd-benefits__title">{b.label}</h4>
              {b.desc && <p className="sd-benefits__desc">{b.desc}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ClosingCta({ service }) {
  return (
    <div className="sd-closing">
      <h3 className="sd-closing__title">Need Fast {service.title}?</h3>
      <p className="sd-closing__text">
        Our team is ready to deliver fast, reliable {service.title.toLowerCase()} you can count on.
        Book your service today and enjoy lasting results.
      </p>
      <a href="/contact-us" className="sd-closing__btn">
        Book Service Now
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
    </div>
  );
}

/* ── FAQ accordion ── */
function Faqs({ faqs }) {
  const [open, setOpen] = useState(null);
  if (!faqs?.length) return null;
  return (
    <div className="sd-faq">
      <h3 className="sd-faq__heading">Read Useful FAQs</h3>
      {faqs.map((faq, i) => (
        <div key={faq.id} className={`sd-faq__item ${open === i ? "sd-faq__item--open" : ""}`}>
          <button
            className="sd-faq__q"
            onClick={() => setOpen(open === i ? null : i)}
            aria-expanded={open === i}
          >
            {faq.title}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"
              className={`sd-faq__chev ${open === i ? "sd-faq__chev--open" : ""}`}>
              <path d="M2 4.5l5 5 5-5" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {open === i && <div className="sd-faq__a">{faq.description}</div>}
        </div>
      ))}
    </div>
  );
}

/* ── Video card ── */
function VideoCard({ video }) {
  const [playing, setPlaying] = useState(false);

  if (!video) {
    return (
      <div className="sd-video sd-video--empty">
        <span>No video available for this service yet.</span>
      </div>
    );
  }

  const videoId = getYouTubeId(video.video_url);

  const handlePlay = () => {
    if (videoId) {
      setPlaying(true);
    } else if (video.video_url) {
      window.open(video.video_url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="sd-video-wrap">
      <div className="sd-video">
        {playing && videoId ? (
          <iframe
            className="sd-video__frame"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title={video.title || "Service video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className="sd-video__thumb"
            onClick={handlePlay}
            aria-label={`Play video: ${video.title || "Service video"}`}
          >
            {video.thumbnail_filename ? (
              <img
                src={`${UPLOADS_URL}/videos/${video.thumbnail_filename}`}
                alt={video.title || "Service video thumbnail"}
                className="sd-video__thumb-img"
              />
            ) : (
              <div className="sd-video__thumb-fallback" />
            )}
            <span className="sd-video__play" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </button>
        )}
      </div>
      {video.description && <p className="sd-video__desc">{video.description}</p>}
    </div>
  );
}

/* ── Lightbox ── */
function Lightbox({ images, index, onClose, onNavigate }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate(1);
      if (e.key === "ArrowLeft") onNavigate(-1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onNavigate]);

  return (
    <div className="sd-lightbox" onClick={onClose} role="dialog" aria-modal="true">
      <button className="sd-lightbox__close" onClick={onClose} aria-label="Close">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <path d="M4 4l12 12M16 4L4 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
      {images.length > 1 && (
        <button
          className="sd-lightbox__nav sd-lightbox__nav--prev"
          onClick={(e) => { e.stopPropagation(); onNavigate(-1); }}
          aria-label="Previous image"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 4l-6 6 6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      <img
        src={`${UPLOADS_URL}/services/${images[index]}`}
        alt={`Gallery item ${index + 1}`}
        className="sd-lightbox__img"
        onClick={(e) => e.stopPropagation()}
      />
      {images.length > 1 && (
        <button
          className="sd-lightbox__nav sd-lightbox__nav--next"
          onClick={(e) => { e.stopPropagation(); onNavigate(1); }}
          aria-label="Next image"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M8 4l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      {images.length > 1 && (
        <div className="sd-lightbox__counter">{index + 1} / {images.length}</div>
      )}
    </div>
  );
}

/* ── Gallery grid ── */
function GalleryGrid({ images, service, video }) {
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (i) => { setLightboxIndex(i); setLightboxOpen(true); };
  const navigateLightbox = (dir) => {
    setLightboxIndex((i) => (i + dir + images.length) % images.length);
  };

  return (
    <div className="sd-gallery">
      <div className="sd-gallery__video-col">
        <VideoCard video={video} />
      </div>

      <div className="sd-gallery__media">
        {images.length > 0 ? (
          <>
            <button
              type="button"
              className="sd-gallery__img-wrap"
              onClick={() => openLightbox(active)}
              aria-label="View larger image"
            >
              <img
                src={`${UPLOADS_URL}/services/${images[active]}`}
                alt={service.title}
                className="sd-gallery__img"
              />
              <span className="sd-gallery__zoom" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <circle cx="8.5" cy="8.5" r="6" stroke="#fff" strokeWidth="1.8" />
                  <path d="M13 13l4.5 4.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
            </button>

            {images.length > 1 && (
              <div className="sd-gallery__thumbs">
                {images.map((img, i) => (
                  <button
                    key={i}
                    className={`sd-gallery__thumb ${i === active ? "sd-gallery__thumb--active" : ""}`}
                    onClick={() => setActive(i)}
                    aria-label={`View image ${i + 1}`}
                  >
                    <img src={`${UPLOADS_URL}/services/${img}`} alt={`Thumbnail ${i + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="sd-gallery__media-empty">No gallery images available.</div>
        )}
      </div>

      {lightboxOpen && images.length > 0 && (
        <Lightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNavigate={navigateLightbox}
        />
      )}
    </div>
  );
}

/* ── Main Page ── */
function SpecialityPage() {
  const { slug } = useParams();

  const [data,    setData]    = useState(null);
  const [contact, setContact] = useState(null);
  const [faqs,    setFaqs]    = useState([]);
  const [video,   setVideo]   = useState(null);
  const [brochureProducts, setBrochureProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchJSON(`${API_BASE}/speciality-detail.php?slug=${encodeURIComponent(slug)}`),
      fetchJSON(`${API_BASE}/company-contact.php`),
      fetchJSON(`${API_BASE}/faq.php`).catch(() => []),
      fetchJSON(`${API_BASE}/products.php`).catch(() => []),
    ])
      .then(([svcData, contactData, faqData, productsData]) => {
        if (cancelled) return null;
        setData(svcData);
        setContact(contactData);
        setFaqs(Array.isArray(faqData) ? faqData.filter(f => f.active !== 0) : []);
        setBrochureProducts(
          Array.isArray(productsData) ? productsData.filter((p) => p.brochure_url) : []
        );

        const title = svcData?.service?.title;
        if (!title) return null;

        return fetchJSON(`${API_BASE}/video-by-title.php?title=${encodeURIComponent(title)}`)
          .catch(() => null);
      })
      .then((videoData) => {
        if (cancelled) return;
        setVideo(videoData || null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("SpecialityPage load error:", err);
        setError("Failed to load service.");
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug]);

  if (loading) return null;

  if (error || !data?.service) return (
    <div className="sd-error">
      <h2>Service not found</h2>
      <p>The service you are looking for does not exist or has been removed.</p>
      <a href="/" className="sd-error__link">Back to Home</a>
    </div>
  );

  const { service, siblings, parent } = data;
  const servedHeader = getServedHeader(service.slug);
  const { intro, features, benefits, servedList } =
    parseSpecialityDescription(service.description, servedHeader);

  const galleryImages = [
    service.image3, service.image4, service.image5,
    service.image6, service.image7,
  ].filter(Boolean);

  return (
    <>
    <div className="sd">

      {/* ── HERO ── */}
      <div className="sd-hero">
        {service.image1 && (
          <img
            src={`${UPLOADS_URL}/services/${service.image1}`}
            alt={service.title}
            className="sd-hero__img"
          />
        )}
        <div className="sd-hero__overlay" />
        <div className="sd-hero__content">
          <Breadcrumb parent={parent} serviceTitle={service.title} />
          <h1 className="sd-hero__title">{service.title}</h1>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="sd-body">
        <div className="sd-body__inner">

          <Sidebar
            siblings={siblings}
            currentSlug={slug}
            contact={contact}
            servedList={servedList}
            brochureProducts={brochureProducts}
          />

          <main className="sd-main">

            {/* TOP: description left, image2 right */}
            <div className="sd-top">
              <div className="sd-top__text">
                <h2 className="sd-main__heading">Reliable {service.title} Solutions</h2>
                <div className="sd-main__desc">
                  {intro
                    ? <p>{intro}</p>
                    : <p>We provide professional {service.title} services with years of experience and commitment to quality.</p>
                  }
                </div>
              </div>
              {service.image2 && (
                <div className="sd-top__img-wrap">
                  <img
                    src={`${UPLOADS_URL}/services/${service.image2}`}
                    alt={service.title}
                    className="sd-top__img"
                  />
                </div>
              )}
            </div>

            <WhatWeOffer features={features} />
            <Benefits benefits={benefits} />
            <ClosingCta service={service} />

            {(galleryImages.length > 0 || video) && (
              <div className="sd-section">
                <h3 className="sd-section__heading">Ultimate Service Provider</h3>
                <GalleryGrid images={galleryImages} service={service} video={video} />
              </div>
            )}

            <Faqs faqs={faqs} />

            {/* Mobile-only: mirrors Brochure/Emergency/ServedList from the
                sidebar. Hidden on desktop via .sd-main__extras CSS. */}
            <div className="sd-main__extras">
              <BrochureCard products={brochureProducts} />
              <EmergencyCta contact={contact} />
              <ServedList servedList={servedList} />
            </div>

          </main>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');

        /* ── RESET ── */
        .sd *, .sd *::before, .sd *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .sd a { text-decoration: none; }
        .sd ul { list-style: none; }
        .sd { font-family: 'Roboto', sans-serif; background: #f5f7fa; overflow-x: hidden; width: 100%; }

        /* ── ERROR ── */
        .sd-error { text-align: center; padding: 80px 24px; font-family: 'Roboto', sans-serif; color: #424244; }
        .sd-error h2 { font-size: 28px; color: #013d97; margin-bottom: 12px; }
        .sd-error p  { margin-bottom: 24px; }
        .sd-error__link { display: inline-block; padding: 10px 24px; background: #029add; color: #fff; border-radius: 6px; font-weight: 700; }

        /* ── HERO ── */
        .sd-hero { position: relative; height: 280px; overflow: hidden; background: #013d97; }
        .sd-hero__img { width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
        .sd-hero__overlay { position: absolute; inset: 0; background: linear-gradient(to right, rgba(1,21,70,.75) 40%, rgba(1,61,151,.4) 100%); }
        .sd-hero__content { position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: flex-end; padding: 32px 40px; max-width: 1280px; margin: 0 auto; left: 0; right: 0; }
        .sd-hero__title { font-size: clamp(26px, 3.5vw, 44px); font-weight: 900; color: #fff; line-height: 1.15; margin-top: 10px; }

        /* ── BREADCRUMB ── */
        .sd-bread { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .sd-bread__link { font-size: 13px; font-weight: 600; color: rgba(255,255,255,.8); background: #029add; padding: 3px 10px; border-radius: 3px; transition: background .2s; }
        .sd-bread__link:hover { background: #013d97; }
        .sd-bread__sep { color: rgba(255,255,255,.6); font-size: 14px; }
        .sd-bread__current { font-size: 13px; color: rgba(255,255,255,.7); }

        /* ── BODY LAYOUT ── */
        .sd-body { padding: 48px 0 72px; }
        .sd-body__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; display: grid; grid-template-columns: 280px 1fr; gap: 32px; align-items: start; }

        /* ── MOBILE TAB — hidden on desktop ── */
        .sd-side__mobile-tab { display: none; }

        /* ── MOBILE OVERLAY — hidden on desktop ── */
        .sd-side__overlay { display: none; }

        /* ── SIDEBAR ── */
        .sd-side { display: flex; flex-direction: column; }

        /* Service List (dark) */
        .sd-list-card { background: #11162a; border-radius: 4px; overflow: hidden; margin-bottom: 24px; box-shadow: 0 2px 12px rgba(0,0,0,.14); }
        .sd-list-card__head { padding: 16px 20px; font-size: 15px; font-weight: 800; color: #fff; }
        .sd-list-card__list { display: flex; flex-direction: column; }
        .sd-list-card__item { display: flex; align-items: center; justify-content: space-between; padding: 13px 18px; font-size: 13.5px; font-weight: 600; color: #fff; background: #171c33; border-top: 1px solid rgba(255,255,255,.06); transition: background .2s; }
        .sd-list-card__item:hover { background: #202746; }
        .sd-list-card__item--active { background: #029add; }
        .sd-list-card__arrow { width: 26px; height: 26px; border-radius: 3px; background: rgba(255,255,255,.14); display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0; }
        .sd-list-card__item--active .sd-list-card__arrow { background: rgba(255,255,255,.28); }

        /* Download Brochure */
        .sd-brochure { background: #fff; border-radius: 4px; padding: 22px 20px; margin-bottom: 24px; box-shadow: 0 2px 12px rgba(0,0,0,.06); }
        .sd-brochure__title { font-size: 18px; font-weight: 800; color: #1a1a2e; margin-bottom: 16px; }
        .sd-brochure__list { display: flex; flex-direction: column; gap: 10px; }
        .sd-brochure__row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 12px; background: #f9fafb; border: 1px solid #eef0f5; border-radius: 4px; transition: border-color .2s, background .2s; }
        .sd-brochure__row:hover { background: #fff8f0; border-color: #029add; }
        .sd-brochure__row-label { font-size: 12.5px; font-weight: 700; color: #1a1a2e; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; min-width: 0; }
        .sd-brochure__row-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
        .sd-brochure__icon-btn { width: 28px; height: 28px; border-radius: 3px; display: flex; align-items: center; justify-content: center; transition: background .2s, color .2s, border-color .2s; }
        .sd-brochure__icon-btn--view { color: #013d97; background: #fff; border: 1px solid #e0e4ef; }
        .sd-brochure__icon-btn--view:hover { background: #eef3fb; border-color: #029add; }
        .sd-brochure__icon-btn--download { color: #fff; background: #029add; }
        .sd-brochure__icon-btn--download:hover { background: #424244; }

        /* Emergency CTA */
        .sd-emergency { position: relative; overflow: hidden; border-radius: 4px; min-height: 200px; background: linear-gradient(135deg, #0e1322, #1b2238); margin-bottom: 24px; }
        .sd-emergency__overlay { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(14,19,34,.5), rgba(14,19,34,.9)); }
        .sd-emergency__content { position: relative; z-index: 1; padding: 26px 22px; display: flex; flex-direction: column; gap: 10px; }
        .sd-emergency__title { font-size: 18px; font-weight: 800; color: #fff; line-height: 1.3; }
        .sd-emergency__text { font-size: 13px; color: rgba(255,255,255,.75); line-height: 1.6; }
        .sd-emergency__btn { display: inline-flex; align-items: center; gap: 8px; align-self: flex-start; margin-top: 6px; padding: 11px 20px; background: #029add; color: #fff; font-size: 12.5px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; border-radius: 3px; transition: background .2s; }
        .sd-emergency__btn:hover { background: #424244; }

        /* Served list */
        .sd-served { background: #fff; border-radius: 4px; padding: 22px 20px; margin-bottom: 24px; box-shadow: 0 2px 12px rgba(0,0,0,.06); }
        .sd-served__title { font-size: 16px; font-weight: 800; color: #1a1a2e; margin-bottom: 14px; line-height: 1.35; }
        .sd-served__list { display: flex; flex-direction: column; gap: 10px; }
        .sd-served__item { display: flex; align-items: flex-start; gap: 10px; font-size: 13px; color: #5a5f72; line-height: 1.5; }
        .sd-served__icon { width: 20px; height: 20px; border-radius: 50%; background: #e6f4fc; color: #029add; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
        .sd-served__icon svg { width: 11px; height: 11px; }

        /* ── MAIN CONTENT ── */
        .sd-main { display: flex; flex-direction: column; gap: 32px; min-width: 0; }
        .sd-section { background: #fff; padding: 32px; box-shadow: 0 2px 12px rgba(0,0,0,.06); }
        .sd-section__heading { font-size: 22px; font-weight: 800; color: #1a1a2e; margin-bottom: 20px; }

        /* Mobile-only mirror of sidebar extra cards — hidden on desktop */
        .sd-main__extras { display: none; }

        .sd-main__heading { font-size: clamp(20px, 2.2vw, 28px); font-weight: 800; color: #1a1a2e; margin-bottom: 16px; }
        .sd-main__desc { display: flex; flex-direction: column; gap: 12px; text-align: justify; }
        .sd-main__desc p { font-size: 14.5px; color: #5a5f72; line-height: 1.75; }

        .sd-top { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; align-items: start; background: #fff; padding: 32px; box-shadow: 0 2px 12px rgba(0,0,0,.06); }
        .sd-top__img-wrap { border-radius: 4px; overflow: hidden; }
        .sd-top__img { width: 100%; height: 100%; object-fit: cover; display: block; border-radius: 4px; }

        /* What We Offer */
        .sd-feat__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .sd-feat__card { padding: 22px; background: #f5f7fa; border: 1px solid #eef0f5; border-radius: 4px; transition: border-color .2s, background .2s; }
        .sd-feat__card:hover { border-color: #029add; background: #fff8f0; }
        .sd-feat__icon { width: 40px; height: 40px; margin-bottom: 14px; display: flex; align-items: center; justify-content: center; }
        .sd-feat__icon img { width: 36px; height: 36px; object-fit: contain; }
        .sd-feat__title { font-size: 15px; font-weight: 800; color: #1a1a2e; margin-bottom: 8px; }
        .sd-feat__desc { font-size: 13px; color: #5a5f72; line-height: 1.6; }

        /* Benefits */
        .sd-benefits__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 26px 40px; }
        .sd-benefits__item { display: flex; align-items: flex-start; gap: 14px; }
        .sd-benefits__icon { width: 26px; height: 26px; border-radius: 50%; background: #e6f4fc; color: #029add; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px; }
        .sd-benefits__icon svg { width: 14px; height: 14px; }
        .sd-benefits__content { display: flex; flex-direction: column; gap: 4px; }
        .sd-benefits__title { font-size: 15px; font-weight: 800; color: #1a1a2e; line-height: 1.4; }
        .sd-benefits__desc { font-size: 13px; color: #5a5f72; line-height: 1.65; }

        /* Closing CTA */
        .sd-closing { background: #fff; padding: 32px; box-shadow: 0 2px 12px rgba(0,0,0,.06); }
        .sd-closing__title { font-size: 22px; font-weight: 800; color: #1a1a2e; margin-bottom: 12px; }
        .sd-closing__text { font-size: 14px; color: #5a5f72; line-height: 1.7; margin-bottom: 20px; max-width: 640px; }
        .sd-closing__btn { display: inline-flex; align-items: center; gap: 8px; padding: 13px 26px; background: #029add; color: #fff; font-size: 13.5px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; border-radius: 3px; transition: background .2s, transform .15s; }
        .sd-closing__btn:hover { background: #424244; transform: translateY(-2px); }

        /* ── GALLERY ── */
        .sd-gallery { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: start; }
        .sd-gallery__media { display: flex; flex-direction: column; gap: 12px; }
        .sd-gallery__img-wrap { position: relative; display: block; width: 100%; border: none; padding: 0; background: none; cursor: zoom-in; border-radius: 4px; overflow: hidden; aspect-ratio: 4/3; }
        .sd-gallery__img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .35s; }
        .sd-gallery__img-wrap:hover .sd-gallery__img { transform: scale(1.05); }
        .sd-gallery__zoom { position: absolute; top: 12px; right: 12px; width: 34px; height: 34px; border-radius: 50%; background: rgba(1,21,70,.55); display: flex; align-items: center; justify-content: center; }
        .sd-gallery__media-empty { aspect-ratio: 4/3; display: flex; align-items: center; justify-content: center; text-align: center; padding: 20px; border-radius: 4px; background: #f9fafb; border: 1px dashed #d8dee9; color: #8890a0; font-size: 13px; }
        .sd-gallery__thumbs { display: flex; gap: 10px; flex-wrap: wrap; }
        .sd-gallery__thumb { width: 90px; height: 68px; border-radius: 4px; overflow: hidden; border: 2px solid transparent; cursor: pointer; padding: 0; background: #e6f0fb; flex-shrink: 0; transition: border-color .2s, transform .2s; }
        .sd-gallery__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .sd-gallery__thumb:hover { transform: translateY(-2px); border-color: #029add; }
        .sd-gallery__thumb--active { border-color: #029add; }

        .sd-gallery__video-col { display: flex; flex-direction: column; }
        .sd-video-wrap { display: flex; flex-direction: column; width: 100%; }
        .sd-video { position: relative; width: 100%; aspect-ratio: 4/3; border-radius: 4px; overflow: hidden; background: #0b1530; }
        .sd-video__desc { padding: 14px 0 0; font-size: 13.5px; color: #5a5f72; line-height: 1.75; text-align: justify; }
        .sd-video--empty { display: flex; align-items: center; justify-content: center; text-align: center; padding: 20px; background: #f9fafb; border: 1px dashed #d8dee9; color: #8890a0; font-size: 13px; }
        .sd-video__frame { width: 100%; height: 100%; border: 0; display: block; }
        .sd-video__thumb { position: relative; width: 100%; height: 100%; border: none; padding: 0; cursor: pointer; display: block; }
        .sd-video__thumb-img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .sd-video__thumb-fallback { width: 100%; height: 100%; background: linear-gradient(135deg, #013d97, #029add); }
        .sd-video__play { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
        .sd-video__play svg { width: 60px; height: 60px; padding: 18px; border-radius: 50%; background: rgba(1,21,70,.6); transition: background .2s, transform .2s; }
        .sd-video__thumb:hover .sd-video__play svg { background: #013d97; transform: scale(1.08); }

        /* ── LIGHTBOX ── */
        .sd-lightbox { position: fixed; inset: 0; z-index: 1000; background: rgba(5,10,25,.92); display: flex; align-items: center; justify-content: center; padding: 40px; }
        .sd-lightbox__img { max-width: min(90vw, 1100px); max-height: 85vh; object-fit: contain; border-radius: 4px; box-shadow: 0 10px 40px rgba(0,0,0,.5); }
        .sd-lightbox__close { position: absolute; top: 20px; right: 20px; width: 40px; height: 40px; border-radius: 50%; background: rgba(255,255,255,.12); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background .2s; }
        .sd-lightbox__close:hover { background: rgba(255,255,255,.25); }
        .sd-lightbox__nav { position: absolute; top: 50%; transform: translateY(-50%); width: 48px; height: 48px; border-radius: 50%; background: rgba(255,255,255,.12); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background .2s; }
        .sd-lightbox__nav:hover { background: rgba(255,255,255,.25); }
        .sd-lightbox__nav--prev { left: 20px; }
        .sd-lightbox__nav--next { right: 20px; }
        .sd-lightbox__counter { position: absolute; bottom: 24px; left: 50%; transform: translateX(-50%); color: #fff; font-size: 13px; background: rgba(255,255,255,.12); padding: 6px 14px; border-radius: 20px; }

        /* ── FAQS ── */
        .sd-faq { background: #fff; padding: 32px; box-shadow: 0 2px 12px rgba(0,0,0,.06); }
        .sd-faq__heading { font-size: 22px; font-weight: 800; color: #1a1a2e; margin-bottom: 20px; }
        .sd-faq__item { border: 1px solid #e8eaf0; border-radius: 4px; margin-bottom: 10px; overflow: hidden; }
        .sd-faq__q { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 18px; background: #f5f7fa; border: none; cursor: pointer; text-align: left; font-size: 14px; font-weight: 700; color: #1a1a2e; transition: background .2s, color .2s; }
        .sd-faq__q:hover { background: #e6f4fc; }
        .sd-faq__item--open .sd-faq__q { background: #013d97; color: #fff; }
        .sd-faq__chev { flex-shrink: 0; transition: transform .25s; }
        .sd-faq__chev--open { transform: rotate(180deg); }
        .sd-faq__item--open .sd-faq__chev { stroke: #fff; }
        .sd-faq__a { padding: 14px 18px; font-size: 13.5px; color: #5a5f72; line-height: 1.7; background: #fff; border-top: 1px solid #e8eaf0; }

        /* ══════════════════════════════════════════
           RESPONSIVE
        ══════════════════════════════════════════ */

        @media (max-width: 1024px) {
          .sd-body__inner { grid-template-columns: 240px 1fr; }
        }

        /* ── TABLET / MOBILE: single column ── */
        @media (max-width: 768px) {

          /* Body: single column, normal padding — no left offset hack */
          .sd-body__inner {
            grid-template-columns: 1fr;
            padding: 0 20px;
            grid-template-areas:
              "main"
              "sidebar";
          }
          .sd-side { grid-area: sidebar; }
          .sd-main { grid-area: main; }

          /* Services list inside sidebar: hidden by default on mobile */
          .sd-side__services { display: none; }

          /* When open: fixed panel slides in from left, centred vertically */
          .sd-side__services--open {
            display: block;
            position: fixed;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 280px;
            max-width: calc(100vw - 24px);
            max-height: 75vh;
            z-index: 300;
            overflow-y: auto;
            background: #f5f7fa;
            padding: 20px 16px 40px;
            border-radius: 0 12px 12px 0;
            box-shadow: 4px 0 24px rgba(0,0,0,.2);
          }

          /* Dimmed overlay behind the open panel — rendered as JSX div */
          .sd-side__overlay {
            display: block;
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,.4);
            z-index: 200;
          }

          /* On small screens the panel shows only the Service List;
             Brochure/Emergency/ServedList move to bottom of main. */
          .sd-side__extras { display: none; }
          .sd-main__extras { display: flex; flex-direction: column; gap: 24px; }

          /* Pull-tab: fixed to the left edge, centred vertically */
          .sd-side__mobile-tab {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 6px;
            position: fixed;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            z-index: 400;
            background: #013d97;
            color: #fff;
            border: none;
            border-radius: 0 6px 6px 0;
            padding: 14px 10px 14px 8px;
            cursor: pointer;
            box-shadow: 3px 0 12px rgba(0,0,0,.25);
            writing-mode: vertical-rl;
            text-orientation: mixed;
            transition: background .2s;
          }
          .sd-side__mobile-tab:hover { background: #424244; }
          .sd-side__mobile-tab-icon { width: 16px; height: 16px; transition: transform .3s; }
          .sd-side__mobile-tab-icon--open { transform: rotate(180deg); }
          .sd-side__mobile-tab-label { font-size: 11px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; font-family: 'Roboto', sans-serif; }

          /* Hero */
          .sd-hero { height: 220px; }
          .sd-hero__content { padding: 24px 20px; }

          /* Two-col top section → single column */
          .sd-top { grid-template-columns: 1fr; padding: 20px; }

          /* Feature grid stays 2-col on tablet */
          .sd-feat__grid { grid-template-columns: 1fr 1fr; }

          /* Benefits → single column */
          .sd-benefits__grid { grid-template-columns: 1fr; }

          /* Gallery: stack video above images */
          .sd-gallery { grid-template-columns: 1fr; }

          /* Lightbox */
          .sd-lightbox { padding: 20px; }
          .sd-lightbox__nav { width: 40px; height: 40px; }
        }

        /* ── SMALL PHONES ── */
        @media (max-width: 480px) {
          .sd-body__inner { padding: 0 16px; }
          .sd-feat__grid { grid-template-columns: 1fr; }
          .sd-top, .sd-section, .sd-faq, .sd-closing { padding: 20px; }
        }

        /* ── FOCUS & MOTION ── */
        a:focus-visible, button:focus-visible { outline: 2px solid #029add; outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) {
          .sd *, .sd *::before, .sd *::after { transition-duration: 0ms !important; }
        }
      `}</style>
    </div>
    <Footer />
    </>
  );
}

export default SpecialityPage;